export interface PolicyItem {
  boldText?: string;
  text: string;
}

export interface PolicySubsection {
  title?: string;
  text?: string;
  items?: PolicyItem[];
  footerText?: string;
}

export interface PolicySection {
  id: string;
  title: string;
  description?: string;
  subsections?: PolicySubsection[];
  listItems?: PolicyItem[];
  footerText?: string;
}

export interface TermsConditionsData {
  header: {
    title: string;
    description: string;
    lastUpdated: string;
    introText: string;
  };
  sections: PolicySection[];
  footerNote: string;
}

export const TERMS_CONDITIONS_DATA: TermsConditionsData = {
  header: {
    title: 'Terms and Conditions',
    description:
      'Welcome to the Charaka Practice Test Portal. These Terms & Conditions govern your access to and use of the Portal.',
    lastUpdated: 'July 30, 2026',
    introText:
      'By registering for or using the Portal, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you do not agree with these Terms, please refrain from using the Portal.',
  },
  sections: [
    {
      id: '1',
      title: 'Purpose of the Portal',
      description:
        'The Charaka Practice Test Portal is an online platform developed to help students prepare for competitive medical entrance examinations through mock tests and practice examinations.\nThe Portal is intended solely for educational, practice, and self-assessment purposes.',
    },
    {
      id: '2',
      title: 'User Registration',
      description: 'To access the portal, users must create an account using valid and accurate information. By registering, you agree to:',
      listItems: [
        { text: 'Provide true, accurate, and complete information.' },
        { text: 'Maintain the confidentiality of your login credentials.' },
        { text: 'Be solely responsible for all activities performed through your account.' },
        { text: 'Promptly update your information whenever necessary.' },
      ],
      footerText:
        'Charaka reserves the right to suspend or terminate any account if false, misleading, or incomplete information is provided.',
    },
    {
      id: '3',
      title: 'Eligibility',
      description:
        'The portal is intended for students preparing for competitive medical entrance examinations. Users below the age of 18 should use the Portal only with the consent of their parent or legal guardian.',
    },
    {
      id: '4',
      title: 'Permitted Use',
      description:
        'The Portal is provided solely for your personal and non-commercial educational use. You agree that you will not:',
      listItems: [
        { text: 'Share your account credentials with any other person.' },
        { text: 'Allow another individual to use your account.' },
        { text: 'Use automated software, bots, or scripts to access the Portal.' },
        { text: 'Attempt to gain unauthorized access to the Portal or its systems.' },
        { text: 'Interfere with or disrupt the operation or security of the Portal.' },
      ],
    },
    {
      id: '5',
      title: 'Mock Tests',
      description:
        'The mock tests available on the Portal are created solely for practice and preparation. You acknowledge that:',
      listItems: [
        { text: 'Mock tests are intended only for self-assessment.' },
        { text: 'Performance in mock tests does not guarantee examination success, rank, admission, or score.' },
        { text: 'Test content may be updated, modified, or removed at any time without prior notice.' },
      ],
    },
    {
      id: '6',
      title: 'Intellectual Property Rights',
      description:
        'All content is available on the Portal, including but not limited to:',
      listItems: [
        { text: 'Mock test questions' },
        { text: 'Images' },
        { text: 'Graphics' },
        { text: 'Logos' },
        { text: 'Text' },
        { text: 'Interface design' },
        { text: 'Software' },
        { text: 'Reports' },
        { text: 'Analytics' },
      ],
      footerText:
        'is the exclusive intellectual property of Charaka or its licensors and is protected under applicable copyright and intellectual property laws. No part of the Portal may be copied, reproduced, modified, distributed, published, or commercially exploited without prior written permission.',
    },
    {
      id: '7',
      title: 'Anti-Piracy Policy',
      description: 'Users are strictly prohibited from:',
      listItems: [
        { text: 'Copying or reproducing mock test questions.' },
        { text: 'Taking screenshots for public distribution.' },
        { text: 'Recording any portion of the Portal.' },
        { text: 'Sharing test questions through social media, messaging applications, websites, or any other medium.' },
        { text: 'Republishing or selling Portal content.' },
        { text: 'Reverse engineering or attempting to extract any proprietary content or software.' },
      ],
      footerText:
        'Violation of this policy may result in immediate suspension or permanent termination of the account and may attract appropriate legal action.',
    },
    {
      id: '8',
      title: 'User Conduct',
      description: 'Users agree not to:',
      listItems: [
        { text: 'Upload malicious software or viruses.' },
        { text: 'Attempt to hack, manipulate, or bypass Portal security.' },
        { text: 'Use abusive, defamatory, offensive, or unlawful language while using the Portal.' },
        { text: 'Engage in any activity that may disrupt or adversely affect the experience of other users.' },
      ],
    },
    {
      id: '9',
      title: 'Account Suspension or Termination',
      description:
        'Charaka reserves the right to suspend or permanently terminate any user account without prior notice if the user:',
      listItems: [
        { text: 'Violates these Terms.' },
        { text: 'Shares account credentials.' },
        { text: 'Engages in piracy or unauthorised distribution of Portal content.' },
        { text: 'Attempts to manipulate or misuse the Portal.' },
        { text: 'Engages in any activity considered harmful to the Portal or its users.' },
      ],
    },
    {
      id: '10',
      title: 'Availability of Services',
      description:
        'While Charaka makes every effort to keep the Portal operational, uninterrupted or error-free access cannot be guaranteed. Temporary interruptions may occur due to:',
      listItems: [
        { text: 'Scheduled maintenance' },
        { text: 'Software updates' },
        { text: 'Internet connectivity issues' },
        { text: 'Technical failures' },
        { text: "Events beyond Charaka's reasonable control" },
      ],
      footerText:
        'Charaka shall not be liable for any inconvenience or loss resulting from such interruptions.',
    },
    {
      id: '11',
      title: 'Disclaimer',
      description:
        'The Charaka Practice Test Portal is provided on an "AS IS" and "AS AVAILABLE" basis. Charaka makes no warranties regarding:',
      listItems: [
        { text: 'Continuous availability of the Portal.' },
        { text: 'Error-free operation.' },
        { text: 'Completeness or accuracy of all mock test content.' },
        { text: 'Compatibility with every browser or device.' },
        { text: 'The mock tests are designed solely for educational practice and should not be interpreted as predictions of actual examination questions or results.' },
      ],
    },
    {
      id: '12',
      title: 'Limitation of Liability',
      description:
        'To the maximum extent permitted under applicable law, Charaka shall not be liable for any direct, indirect, incidental, special, or consequential damages arising from:',
      listItems: [
        { text: 'Use of the Portal.' },
        { text: 'Inability to access the Portal.' },
        { text: 'Technical interruptions.' },
        { text: 'Loss of data.' },
        { text: 'Any reliance placed on mock test performance.' },
      ],
    },
    {
      id: '13',
      title: 'Privacy',
      description:
        'By using the Portal, you consent to the collection and processing of your information in accordance with the Charaka Privacy Policy. Your personal information will be used only for purposes related to providing and improving the services offered through the Portal.',
    },
    {
      id: '14',
      title: 'Changes to the Terms',
      description:
        'Charaka reserves the right to modify or update these Terms & Conditions at any time. Any revised terms shall become effective immediately upon publication on the portal. Continued use of the Portal after such updates constitutes acceptance of the revised Terms.',
    },
    {
      id: '15',
      title: 'Governing Law',
      description:
        'These Terms & Conditions shall be governed by and interpreted in accordance with the laws of India. Any dispute arising out of or relating to these Terms or the use of the Charaka Practice Test Portal shall be subject to the exclusive jurisdiction of the competent courts in India.',
    },
    {
      id: '16',
      title: 'Contact Us',
      description:
        'For any questions, feedback, technical support, or concerns regarding the Charaka Practice Test Portal, please contact the Charaka Support Team through the official communication channels available on the Portal.',
    },
  ],
  footerNote: '',
};
